import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./api";

// Shapes mirror what /api/tenant/* returns. Kept loose (any-tolerant) where
// the backend types haven't been worth the import overhead — these views are
// display-only and won't crash on extra fields.

export interface OverviewResponse {
  business: {
    id: string;
    name: string;
    slug: string;
    timezone: string;
    receptionist_name: string | null;
    languages: string[] | null;
    is_active: boolean;
    scheduling_provider: string;
  } | null;
  today: { conversations: number };
  counts: {
    recent_conversations: number;
    recent_leads: number;
    upcoming_bookings: number;
    pending_handoffs: number;
    open_callbacks: number;
  };
  integrations: {
    google_calendar:
      | { connected: false }
      | { connected: true; connected_email: string | null; connected_at: string };
  };
  scheduling_provider: string;
  readiness_issues: string[];
}

export interface ConversationRow {
  id: string;
  business_id: string;
  channel: "web" | "voice" | "whatsapp" | "sms";
  caller_name: string | null;
  caller_phone: string | null;
  caller_email: string | null;
  status: string;
  started_at: string;
  ended_at: string | null;
  summary: string | null;
}

export interface MessageRow {
  id: string;
  conversation_id: string;
  role: "user" | "assistant" | "tool" | "system";
  content: string;
  tool_name: string | null;
  tool_input: string | null;
  tool_output: string | null;
  created_at: string;
}

export interface LeadRow {
  id: string;
  conversation_id: string | null;
  name: string | null;
  phone: string | null;
  email: string | null;
  service_interest: string | null;
  preferred_time: string | null;
  notes: string | null;
  qualified: number;             // 1 yes, 0 no, -1 unknown
  qualification_reason: string | null;
  status: string;
  created_at: string;
}

export interface BookingRow {
  id: string;
  conversation_id: string | null;
  service_id: string | null;
  customer_name: string;
  customer_phone: string | null;
  customer_email: string | null;
  start_time: string;
  end_time: string;
  notes: string | null;
  status: string;
  provider: string | null;
  provider_event_id: string | null;
  created_at: string;
}

export function useOverview() {
  return useQuery({
    queryKey: ["tenant", "overview"],
    queryFn: () => apiFetch<OverviewResponse>("/api/tenant/overview"),
  });
}

export function useConversations(filters?: { channel?: string; status?: string }) {
  const qs = new URLSearchParams();
  if (filters?.channel) qs.set("channel", filters.channel);
  if (filters?.status) qs.set("status", filters.status);
  const q = qs.toString();
  return useQuery({
    queryKey: ["tenant", "conversations", q],
    queryFn: () =>
      apiFetch<ConversationRow[]>(`/api/tenant/conversations${q ? `?${q}` : ""}`),
  });
}

export function useConversationDetail(id: string | null) {
  return useQuery({
    queryKey: ["tenant", "conversation", id],
    enabled: !!id,
    queryFn: () =>
      apiFetch<{ conversation: ConversationRow; messages: MessageRow[] }>(
        `/api/tenant/conversations/${id}`,
      ),
  });
}

export function useLeads() {
  return useQuery({
    queryKey: ["tenant", "leads"],
    queryFn: () => apiFetch<LeadRow[]>("/api/tenant/leads"),
  });
}

export function useBookings(filter: "upcoming" | "past" | "all" = "upcoming") {
  return useQuery({
    queryKey: ["tenant", "bookings", filter],
    queryFn: () =>
      apiFetch<BookingRow[]>(`/api/tenant/bookings?filter=${filter}`),
  });
}

export interface HandoffRow {
  id: string;
  conversation_id: string | null;
  reason: string;
  urgency: string;
  summary: string | null;
  status: string;
  created_at: string;
}

export function useHandoffs() {
  return useQuery({
    queryKey: ["tenant", "handoffs"],
    queryFn: () => apiFetch<HandoffRow[]>("/api/tenant/handoffs"),
  });
}

export interface CallbackRow {
  id: string;
  conversation_id: string | null;
  name: string;
  phone: string;
  best_time: string | null;
  topic: string | null;
  status: string;
  created_at: string;
}

export function useCallbacks() {
  return useQuery({
    queryKey: ["tenant", "callbacks"],
    queryFn: () => apiFetch<CallbackRow[]>("/api/tenant/callbacks"),
  });
}

export interface IntegrationsResponse {
  google_calendar: {
    configured_on_platform: boolean;
    connected: boolean;
    connected_email: string | null;
    connected_at: string | null;
    scope: string | null;
  };
  resend: {
    configured_on_platform: boolean;
    from_email: string | null;
    from_name: string | null;
    reply_to_email: string | null;
  };
  twilio: {
    configured_on_platform: boolean;
    phone_number: string | null;
  };
  meta_whatsapp: {
    configured_on_platform: boolean;
    whatsapp_number: string | null;
  };
}

export function useIntegrations() {
  return useQuery({
    queryKey: ["tenant", "integrations"],
    queryFn: () => apiFetch<IntegrationsResponse>("/api/tenant/integrations"),
  });
}

export interface BusinessProfile {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  phone_number: string | null;
  whatsapp_number: string | null;
  website: string | null;
  industry: string | null;
  greeting: string;
  voice_persona: string | null;
  receptionist_name: string | null;
  business_hours: string | null;
  qualification_criteria: string | null;
  booking_policy: string | null;
  handoff_email: string | null;
  handoff_phone: string | null;
  voice: string | null;
  languages: string;
  scheduling_provider: string;
  ops_language: string;
  is_active: number;
  legal_name: string | null;
  billing_email: string | null;
  tax_id: string | null;
  tax_id_type: string | null;
  billing_address: string | null;
  widget_enabled: number;
  widget_voice_enabled: number;
  widget_allowed_origins: string | null;
  calendly_url: string | null;
  pricing_url: string | null;
  social_links: string | null;
  brand_color: string | null;
  logo_url: string | null;
}

export function useBusinessProfile() {
  // Re-uses /api/me to fetch the active business; for editing, we want the
  // full row. /api/admin/businesses/:id requires the admin key — so we add a
  // tenant-scoped read endpoint piggybacking on the patch endpoint's GET.
  // Simpler approach: fetch the patch endpoint with no body via OPTIONS… no,
  // just GET it via a dedicated endpoint. But we don't want to add a new
  // endpoint just for reads — overview already returns the summary.
  // For Day 4: Settings page calls /api/tenant/business/profile (we'll add it
  // alongside the patch handler).
  return useQuery({
    queryKey: ["tenant", "business-profile"],
    queryFn: () => apiFetch<BusinessProfile>("/api/tenant/business"),
  });
}

// ============================================================
// Billing
// ============================================================

export interface BillingOverview {
  counts: {
    active_subscriptions: number;
    pending_invoices: number;
    overdue_invoices: number;
    paid_invoices: number;
  };
  outstanding_cents: number;
}

export interface SubscriptionRow {
  id: string;
  business_id: string;
  name: string;
  description: string | null;
  amount_cents: number;
  currency: string;
  billing_cycle: string;
  tax_rate_bps: number;
  status: string;
  start_date: string;
  next_invoice_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unit_price_cents: number;
  total_cents: number;
}

export interface InvoiceRow {
  id: string;
  business_id: string;
  subscription_id: string | null;
  invoice_number: string;
  issue_date: string;
  due_date: string;
  period_start: string | null;
  period_end: string | null;
  line_items: string;
  parsed_line_items: InvoiceLineItem[];
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  currency: string;
  status: string;
  total_paid_cents: number;
  balance_cents: number;
  is_overdue: boolean;
  customer_name_snapshot: string;
  customer_legal_name_snapshot: string | null;
  customer_email_snapshot: string | null;
  customer_tax_id_snapshot: string | null;
  customer_tax_id_type_snapshot: string | null;
  customer_address_snapshot: string | null;
  tico_factura_consecutivo: string | null;
  tico_factura_clave: string | null;
  tico_factura_emitted_at: string | null;
  external_invoice_id: string | null;
  external_invoice_provider: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface InvoicePaymentRow {
  id: string;
  invoice_id: string;
  business_id: string;
  amount_cents: number;
  currency: string;
  payment_method: string;
  payment_date: string;
  reference: string | null;
  notes: string | null;
  recorded_by_user_kind: string | null;
  recorded_by_user_id: string | null;
  created_at: string;
}

export function useBillingOverview() {
  return useQuery({
    queryKey: ["tenant", "billing", "overview"],
    queryFn: () => apiFetch<BillingOverview>("/api/tenant/billing/overview"),
  });
}

export function useSubscriptions() {
  return useQuery({
    queryKey: ["tenant", "billing", "subscriptions"],
    queryFn: () =>
      apiFetch<SubscriptionRow[]>("/api/tenant/billing/subscriptions"),
  });
}

export function useInvoices(filters?: { status?: string }) {
  const qs = new URLSearchParams();
  if (filters?.status) qs.set("status", filters.status);
  const q = qs.toString();
  return useQuery({
    queryKey: ["tenant", "billing", "invoices", q],
    queryFn: () =>
      apiFetch<InvoiceRow[]>(`/api/tenant/billing/invoices${q ? `?${q}` : ""}`),
  });
}

export function useInvoiceDetail(id: string | null) {
  return useQuery({
    queryKey: ["tenant", "billing", "invoice", id],
    enabled: !!id,
    queryFn: () =>
      apiFetch<{ invoice: InvoiceRow; payments: InvoicePaymentRow[] }>(
        `/api/tenant/billing/invoices/${id}`,
      ),
  });
}

export function createSubscription(body: {
  name: string;
  description?: string | null;
  amount_cents: number;
  currency: "CRC" | "USD";
  billing_cycle: "monthly" | "quarterly" | "annual" | "one_time";
  tax_rate_bps?: number;
  status?: "active" | "paused" | "cancelled";
  start_date: string;
  next_invoice_date?: string | null;
  notes?: string | null;
}) {
  return apiFetch<{ ok: true; subscription: SubscriptionRow }>(
    "/api/tenant/billing/subscriptions",
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function createInvoice(body: {
  subscription_id?: string | null;
  issue_date: string;
  due_date: string;
  period_start?: string | null;
  period_end?: string | null;
  line_items: InvoiceLineItem[];
  currency: "CRC" | "USD";
  tax_rate_bps?: number;
  notes?: string | null;
  status?: "draft" | "pending";
}) {
  return apiFetch<{ ok: true; invoice: InvoiceRow }>(
    "/api/tenant/billing/invoices",
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function recordPayment(
  invoiceId: string,
  body: {
    amount_cents: number;
    currency: "CRC" | "USD";
    payment_method: "sinpe_movil" | "wire" | "cash" | "card" | "other";
    payment_date: string;
    reference?: string | null;
    notes?: string | null;
  },
) {
  return apiFetch<{
    ok: true;
    payment: InvoicePaymentRow;
    invoice_status: string;
    total_paid_cents: number;
  }>(`/api/tenant/billing/invoices/${invoiceId}/payments`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function recordTicoFactura(
  invoiceId: string,
  body: { consecutivo: string; clave?: string | null },
) {
  return apiFetch<{ ok: true; invoice: InvoiceRow }>(
    `/api/tenant/billing/invoices/${invoiceId}/tico-factura`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function patchInvoice(
  invoiceId: string,
  body: { status?: string; notes?: string | null; due_date?: string },
) {
  return apiFetch<{ ok: true; invoice: InvoiceRow }>(
    `/api/tenant/billing/invoices/${invoiceId}`,
    { method: "PATCH", body: JSON.stringify(body) },
  );
}

export interface BillingProfile {
  legal_name: string | null;
  billing_email: string | null;
  tax_id: string | null;
  tax_id_type: string | null;
  billing_address: string | null;
}

export function patchBillingProfile(body: Partial<BillingProfile>) {
  return apiFetch<{ ok: true; business: BusinessProfile }>(
    "/api/tenant/billing/profile",
    { method: "PATCH", body: JSON.stringify(body) },
  );
}
